import {} from "react";
import { createServerFn } from "@tanstack/react-start";
import { getClerk } from "./clerk-server";
import { type ClerkAPIResponseError } from "@clerk/clerk-js";
import type { UserSelect } from "./schemas";

export interface AuthRequestStateNotSignedIn {
  isSignedIn: false;
}

export interface AuthRequestStateSignedIn {
  isSignedIn: true;
  user: UserSelect;
}

export type AuthRequestState =
  | AuthRequestStateNotSignedIn
  | AuthRequestStateSignedIn;

export function setAuthRequestState(
  sharedMap: Map<string, unknown>,
  state: AuthRequestState,
): void {
  sharedMap.set("auth", state);
}

export function getAuthRequestState(
  sharedMap: Map<string, unknown>,
): AuthRequestState {
  return (sharedMap.get("auth") ?? { isSignedIn: false }) as AuthRequestState;
}

export function isClerkAPIResponseError(
  err: unknown,
): err is ClerkAPIResponseError {
  return err !== null && typeof err === "object" && "clerkError" in err;
}

export const signOut = createServerFn({ method: "POST" }).handler(
  async ({ context: _context }) => {
    // TODO: Access cookies via context.request or similar TanStack Start API
    // First try setting to empty value
    // context.cookie.set("userId", "", cookieOptions);
    // context.cookie.set("coachName", "", cookieOptions);
    // Then delete with same options
    // context.cookie.delete("userId", cookieOptions);
    // context.cookie.delete("coachName", cookieOptions);
    // TODO: Implement cookie management with TanStack Start's cookie API
    // Redirect to ensure clean state
  },
);

export type UpdateUserParams = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

export const updateClerkUser = createServerFn({ method: "POST" })
  .validator((data: { userId: string; params: UpdateUserParams }) => data)
  .handler(async ({ data }) => {
    const { userId, params } = data;
    const clerkClient = getClerk();
    if (!clerkClient) {
      throw new Error("Clerk client not initialized");
    }
    await clerkClient.users.updateUser(userId, params);
  });

export const updateClerkEmailAddresses = createServerFn({ method: "POST" })
  .validator((data: { emailAddressId: string }) => data)
  .handler(async ({ data }) => {
    const { emailAddressId } = data;
    const clerkClient = getClerk();
    if (!clerkClient) {
      throw new Error("Clerk client not initialized");
    }
    const response = await clerkClient.emailAddresses.updateEmailAddress(
      emailAddressId,
      {
        primary: true,
        verified: true,
      },
    );

    return response.id;
  });
